import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import { OrganizationMember, UserInfo } from '@cartwhl/common/types';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import Text from '@cartwhl/ui/base/Text';
import ArrowUpRightIcon from '@cartwhl/ui/icons/ArrowUpRightIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { noticesStore, userStore } from 'stores/global';

import PlanFeatures from './PlanFeatures';

export default function RemoveTeamMemberModal({
  close,
  member,
}: {
  close: () => void;
  member: OrganizationMember;
}) {
  const { userInfo } = useSnapshot(userStore.state);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const name = useMemo(() => {
    return (
      member.preferredName ||
      `${member.firstName} ${member.lastName}`.trim() ||
      member.email
    );
  }, [member]);

  const planLabel = useMemo<'Creator' | 'Unlimited' | 'Professional'>(() => {
    if (member.planType === 'free') {
      return 'Creator';
    } else if (member.planType === 'unlimited') {
      return 'Unlimited';
    } else {
      return 'Professional';
    }
  }, [member.planType]);

  const handleRemove = async () => {
    setSubmitting(true);
    const ok = await userStore.removeUserFromOrganization(
      member.userID || member.email,
    );
    setSubmitting(false);
    if (ok) {
      noticesStore.success('User removed');
      close();
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  };

  const title = confirming ? (
    'Remove user from team'
  ) : (
    <div className="flex w-full flex-row justify-between">
      <div className="flex flex-row items-center gap-2">
        <Avatar userInfo={member as any as UserInfo} size={5} />
        <div className="flex flex-col">
          <Text as="p" variant="studio/body/sm">
            {name}
          </Text>
          <Text
            as="p"
            variant="studio/body/sm"
            className="opacity-60 dark:opacity-70"
          >
            {member.email}
          </Text>
        </div>
      </div>

      <div className="flex items-center">
        <a
          className="ml-auto text-sm"
          href={getHomepageUrl('/pricing')}
          target="_blank"
          rel="noreferrer"
        >
          <div className="flex items-center gap-2">
            <Text as="p" variant="studio/body/md">
              View plan details
            </Text>
            <ArrowUpRightIcon />
          </div>
        </a>
      </div>
    </div>
  );

  return (
    <Modal
      title={title}
      maxWidth={684}
      open={true}
      onOpenChange={(open) => !open && close()}
    >
      <div className="flex flex-col gap-4">
        {!confirming ? (
          <>
            <div className="border-gray-30 dark:border-gray-60 flex items-center justify-between rounded-lg border p-4">
              <PlanFeatures plan={planLabel} />
            </div>

            <div className="mt-2 flex flex-row flex-nowrap">
              <Button
                variant="secondary-destructive"
                onClick={() => setConfirming(true)}
              >
                Remove user
              </Button>
              <Button className="ml-auto" variant="primary" onClick={close}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <div className="flex flex-row gap-2">
                <Avatar userInfo={member as any as UserInfo} size={5} />
                <Text as="p" variant="studio/body/bold/sm">
                  {name}
                </Text>
              </div>
              <Text as="p" variant="studio/body/sm">
                {userInfo.userID === member.userID
                  ? `Are you sure you want to leave the team?`
                  : `Are you sure you want to remove ${name} from your team?`}
              </Text>
            </div>

            <div className="mt-2 flex flex-row flex-nowrap">
              <Button variant="secondary" onClick={close} disabled={submitting}>
                Nevermind
              </Button>
              <Button
                className="ml-auto"
                variant="primary-destructive"
                onClick={handleRemove}
                disabled={submitting}
              >
                Remove user
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
